import { ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import AccessColumnHeader from '@/components/reports/AccessColumnHeader.vue';
import ReportAccessCell from '@/components/reports/ReportAccessCell.vue';
import ReportNameAndFolderCell from '@/components/reports/ReportNameAndFolderCell.vue';
import ReportNotificationsCell from '@/components/reports/ReportNotificationsCell.vue';
import ReportOverflowMenuCell from '@/components/reports/ReportOverflowMenuCell.vue';
import ReportPartnerOverlapsCell from '@/components/reports/ReportPartnerOverlapsCell.vue';
import ReportUserCell from '@/components/reports/ReportUserCell.vue';

import { LIMITED_ACCESS } from '@/constants/shared_list';
import { useBillingStore, useCollaborateStore } from '@/stores';
import { ReportCell } from '@/types/reports';

export function useReportsColumns() {
  const { isFreeTier } = storeToRefs(useBillingStore());
  const { listAccess } = storeToRefs(useCollaborateStore());
  function createFolderFirstComparator<T>(compareFn: (a: T, b: T) => number) {
    return function (
      _val1: undefined,
      _val2: undefined,
      cellA: ICellRendererParams<ReportCell>,
      cellB: ICellRendererParams<ReportCell>,
      isDescending?: boolean,
    ) {
      const isAFolder = cellA.data?.isFolder;
      const isBFolder = cellB.data?.isFolder;

      if (isAFolder && !isBFolder) return isDescending ? 1 : -1;
      if (!isAFolder && isBFolder) return isDescending ? -1 : 1;

      return compareFn(cellA.data as T, cellB.data as T);
    };
  }

  const nameComparator = createFolderFirstComparator<ReportCell>((a, b) => {
    if (a?.report.name === b?.report.name) return 0;
    return (a?.report.name || 0) > (b?.report.name || 0) ? 1 : -1;
  });

  const timeComparator = createFolderFirstComparator<ReportCell>((a, b) => {
    if (a?.report.updated_at === b?.report.updated_at) return 0;
    return (a?.report.updated_at || 0) > (b?.report.updated_at || 0) ? 1 : -1;
  });

  const hasPartialAccess = computed(
    () => isFreeTier.value && listAccess.value === LIMITED_ACCESS,
  );

  const columns = computed<ColDef[]>(() => {
    return [
      {
        cellRenderer: ReportNameAndFolderCell,
        field: 'reportName',
        valueGetter: (params: ICellRendererParams<ReportCell>) => {
          return params.data?.report.name;
        },
        getQuickFilterText: (params: ICellRendererParams<ReportCell>) => {
          if (!params?.data?.report) return '';
          return params.data.report.name;
        },
        headerName: 'List Name',
        sortable: true,
        comparator: nameComparator,
        flex: 1,
        minWidth: 200,
      },
      {
        cellRenderer: ReportPartnerOverlapsCell,
        field: 'partnerOverlaps',
        sortable: false,
        maxWidth: 200,
        minWidth: 160,
      },
      {
        cellRenderer: ReportAccessCell,
        field: 'access',
        headerName: 'Shared With',
        headerComponent: AccessColumnHeader,
        maxWidth: 200,
        minWidth: 160,
      },
      {
        cellRenderer: ReportUserCell,
        field: 'owner',
        maxWidth: 160,
        sortable: true,
        comparator: timeComparator,
        minWidth: 160,
      },
      {
        cellRenderer: ReportUserCell,
        field: 'lastEdited',
        sortable: true,
        initialSort: isFreeTier.value ? undefined : 'desc',
        comparator: timeComparator,
        maxWidth: 160,
        minWidth: 160,
      },
      {
        cellRenderer: ReportNotificationsCell,
        field: 'notifications',
        maxWidth: 120,
      },
      {
        cellRenderer: ReportOverflowMenuCell,
        field: ' ',
        headerName: 'Actions',
        minWidth: 70,
        maxWidth: 70,
      },
    ] as ColDef[];
  });

  return { columns, hasPartialAccess, isFreeTier };
}
